import { useRef, useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { useGetUserQuery, useGetFollowStatusQuery, useFollowUserMutation, useUnfollowUserMutation, useGetUserActivityQuery, useGetUserStatsQuery } from '../../store/api/usersApi'
import { useAppSelector } from '../../hooks/useAppDispatch'
import { timeAgoLong } from '../../lib/avatar'
import Avatar from './Avatar'
import { computeStreak, computeAchievements } from '../../lib/achievements'
import {
  USER_HOVER_CARD_ENABLED,
  USER_HOVER_CARD_WIDTH,
  DROPDOWN_VIEWPORT_MARGIN,
  USER_HOVER_CARD_OPEN_DELAY_MS,
  USER_HOVER_CARD_CLOSE_DELAY_MS,
  COLOR_ACCENT_ROLE_BG,
} from '../../lib/config'

interface UserHoverCardProps {
  userId: string
  currentUserId?: string | null
  displayName: string
  avatarUrl?: string | null
  children: React.ReactNode
}

export default function UserHoverCard({ userId, currentUserId, displayName, avatarUrl, children }: UserHoverCardProps) {
  const navigate = useNavigate()
  if (!USER_HOVER_CARD_ENABLED) return <>{children}</>
  if (userId === currentUserId) return <>{children}</>
  if (displayName === 'Unknown User') return <>{children}</>
  return (
    <HoverCardInner userId={userId} displayName={displayName} avatarUrl={avatarUrl} navigate={navigate}>
      {children}
    </HoverCardInner>
  )
}

function HoverCardInner({ userId, displayName, avatarUrl, navigate, children }: {
  userId: string; displayName: string; avatarUrl?: string | null
  navigate: ReturnType<typeof useNavigate>; children: React.ReactNode
}) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 })
  const triggerRef = useRef<HTMLSpanElement>(null)
  const cardRef = useRef<HTMLDivElement>(null)
  const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const { isAuthenticated } = useAppSelector((s) => s.auth)
  const { data, isLoading } = useGetUserQuery(userId, { skip: !open })
  const { data: followStatusData } = useGetFollowStatusQuery(userId, { skip: !open || !isAuthenticated })
  const { data: activityData } = useGetUserActivityQuery({ id: userId, year: null }, { skip: !open })
  const { data: statsData } = useGetUserStatsQuery(userId, { skip: !open })
  const [followUser, { isLoading: following }] = useFollowUserMutation()
  const [unfollowUser, { isLoading: unfollowing }] = useUnfollowUserMutation()
  const isFollowing = followStatusData?.isFollowing ?? false
  const user = data?.user

  const streak = activityData ? computeStreak(activityData.activity) : 0
  const unlockedBadges = (activityData && statsData && user)
    ? computeAchievements({
        discussions: user._count?.discussions ?? 0,
        followers: user._count?.followers ?? 0,
        following: user._count?.following ?? 0,
        totalContributions: statsData.totalContributions,
        streak,
        activity: activityData.activity,
      }).filter(a => a.unlocked).slice(0, 4)
    : []

  const calcPos = useCallback(() => {
    if (!triggerRef.current) return
    const rect = triggerRef.current.getBoundingClientRect()
    let left = rect.left
    if (left + USER_HOVER_CARD_WIDTH > window.innerWidth - DROPDOWN_VIEWPORT_MARGIN)
      left = window.innerWidth - USER_HOVER_CARD_WIDTH - DROPDOWN_VIEWPORT_MARGIN
    setPos({ top: rect.bottom + 6, left })
  }, [])

  const cancelOpen  = useCallback(() => { if (openTimerRef.current)  clearTimeout(openTimerRef.current) }, [])
  const cancelClose = useCallback(() => { if (closeTimerRef.current) clearTimeout(closeTimerRef.current) }, [])

  const scheduleOpen = useCallback(() => {
    cancelClose()
    if (open) return
    openTimerRef.current = setTimeout(() => { calcPos(); setOpen(true) }, USER_HOVER_CARD_OPEN_DELAY_MS)
  }, [open, cancelClose, calcPos])

  const scheduleClose = useCallback(() => {
    cancelOpen()
    closeTimerRef.current = setTimeout(() => setOpen(false), USER_HOVER_CARD_CLOSE_DELAY_MS)
  }, [cancelOpen])

  useEffect(() => {
    if (!open) return
    const onScroll = () => setOpen(false)
    window.addEventListener('scroll', onScroll, true)
    return () => window.removeEventListener('scroll', onScroll, true)
  }, [open])

  useEffect(() => () => { cancelOpen(); cancelClose() }, [cancelOpen, cancelClose])

  const goToProfile = useCallback((e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation(); setOpen(false); navigate(`/profile/${userId}`)
  }, [navigate, userId])

  const card = open
    ? createPortal(
        <div ref={cardRef} onMouseEnter={cancelClose} onMouseLeave={scheduleClose} onClick={(e) => e.stopPropagation()}
          style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 99999, width: `${USER_HOVER_CARD_WIDTH}px`, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.22)', padding: '1rem', animation: 'hcFadeIn 0.12s ease' }}>
          <style>{`@keyframes hcFadeIn { from { opacity:0; transform:translateY(-4px) } to { opacity:1; transform:translateY(0) } }`}</style>
          {isLoading || !user
            ? <CardSkeleton name={displayName} avatarUrl={avatarUrl} />
            : <CardContent user={user} onClickAvatar={goToProfile} isAuthenticated={isAuthenticated} isFollowing={isFollowing} followLoading={following || unfollowing} onFollow={() => void followUser(userId)} onUnfollow={() => void unfollowUser(userId)} streak={streak} badges={unlockedBadges} totalContributions={statsData?.totalContributions ?? null} />
          }
        </div>,
        document.body
      )
    : null

  return (
    <>
      <span ref={triggerRef} onMouseEnter={scheduleOpen} onMouseLeave={scheduleClose} style={{ display: 'inline-flex', alignItems: 'center' }}>
        {children}
      </span>
      {card}
    </>
  )
}

function CardSkeleton({ name, avatarUrl }: { name: string; avatarUrl?: string | null }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
      <Avatar name={name} avatarUrl={avatarUrl} size={44} />
      <div>
        <div style={{ fontWeight: 600, fontSize: '0.92rem', color: 'var(--text-primary)' }}>{name}</div>
        <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)', marginTop: '3px' }}>Loading...</div>
      </div>
    </div>
  )
}

function CardContent({ user, onClickAvatar, isAuthenticated, isFollowing, followLoading, onFollow, onUnfollow, streak, badges, totalContributions }: {
  user: any
  onClickAvatar: (e: React.MouseEvent) => void
  isAuthenticated: boolean
  isFollowing: boolean
  followLoading: boolean
  onFollow: () => void
  onUnfollow: () => void
  streak: number
  badges: import('../../lib/achievements').Achievement[]
  totalContributions: number | null
}) {
  const name = user.profile?.displayName ?? user.email ?? 'User'
  const [hoveringUnfollow, setHoveringUnfollow] = useState(false)

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <Avatar name={name} avatarUrl={user.profile?.avatarUrl} size={44} onClick={onClickAvatar} />
          {streak >= 2 && (
            <div style={{ position: 'absolute', bottom: -4, right: -4, background: 'rgba(249,115,22,0.95)', borderRadius: '8px', padding: '0.05rem 0.28rem', fontSize: '0.6rem', fontWeight: 700, color: 'white', border: '2px solid var(--bg-card)', lineHeight: 1.4, whiteSpace: 'nowrap' }}>
              {streak >= 30 ? '🔥🔥🔥' : streak >= 14 ? '🔥🔥' : '🔥'} {streak}
            </div>
          )}
        </div>
          <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
            <span onClick={onClickAvatar} style={{ fontWeight: 600, fontSize: '0.92rem', color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '140px', cursor: 'pointer' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLSpanElement).style.color = 'var(--accent-primary)' }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLSpanElement).style.color = 'var(--text-primary)' }}>
              {name}
            </span>
            {user.roles?.includes('admin') && (
              <span style={{ background: COLOR_ACCENT_ROLE_BG, color: 'var(--accent-primary)', padding: '0.1rem 0.45rem', borderRadius: '6px', fontSize: '0.67rem', fontWeight: 600 }}>Admin</span>
            )}
            {badges.length > 0 && (
              <span style={{ background: 'rgba(234,179,8,0.12)', color: 'var(--warning)', border: '1px solid rgba(234,179,8,0.3)', padding: '0.1rem 0.45rem', borderRadius: '6px', fontSize: '0.67rem', fontWeight: 600 }}>
                🏆 {badges.length} badge{badges.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          {user.profile?.flair && (
            <div style={{ marginTop: '3px' }}>
              <span style={{ fontSize: '0.7rem', background: 'rgba(124,58,237,0.1)', color: 'var(--accent-primary)', border: '1px solid rgba(124,58,237,0.22)', borderRadius: '20px', padding: '0.08rem 0.45rem', fontWeight: 500 }}>
                {user.profile.flair}
              </span>
            </div>
          )}
          <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', marginTop: '2px' }}>Joined {timeAgoLong(user.createdAt)}</div>
        </div>
      </div>

      {(user._count || totalContributions !== null) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0', marginBottom: '0.7rem', flexWrap: 'wrap' }}>
          {([
            { label: 'Discussions',   value: user._count?.discussions   ?? 0 },
            { label: 'Followers',     value: user._count?.followers     ?? 0 },
            { label: 'Following',     value: user._count?.following     ?? 0 },
            { label: 'Contributions', value: totalContributions         ?? 0 },
          ] as { label: string; value: number }[]).map(({ label, value }, i, arr) => (
            <div key={label} style={{ display: 'flex', alignItems: 'baseline', gap: '0.25rem', paddingRight: '0.75rem', marginRight: '0.75rem', borderRight: i < arr.length - 1 ? '1px solid var(--border)' : 'none' }}>
              <span style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)' }}>{value >= 1000 ? `${(value / 1000).toFixed(1)}k` : value}</span>
              <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{label}</span>
            </div>
          ))}
        </div>
      )}

      {user.profile?.bio && (
        <>
          <div style={{ height: '1px', background: 'var(--border)', margin: '0 0 0.65rem' }} />
          <p style={{ margin: '0 0 0.75rem', fontSize: '0.81rem', color: 'var(--text-secondary)', lineHeight: 1.55, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            {user.profile.bio}
          </p>
        </>
      )}

      {isAuthenticated && (
        <>
          {!user.profile?.bio && <div style={{ height: '1px', background: 'var(--border)', margin: '0 0 0.75rem' }} />}
          <button
            onClick={(e) => { e.stopPropagation(); isFollowing ? onUnfollow() : onFollow() }}
            disabled={followLoading}
            onMouseEnter={() => setHoveringUnfollow(isFollowing)}
            onMouseLeave={() => setHoveringUnfollow(false)}
            style={{ width: '100%', padding: '0.45rem 0', borderRadius: '8px', border: isFollowing ? '1px solid var(--border)' : 'none', background: isFollowing ? (hoveringUnfollow ? 'rgba(239,68,68,0.06)' : 'transparent') : 'var(--accent-primary)', color: isFollowing ? (hoveringUnfollow ? 'var(--error)' : 'var(--text-muted)') : 'white', fontSize: '0.8rem', fontWeight: 600, cursor: followLoading ? 'not-allowed' : 'pointer', transition: 'all 0.15s' }}
          >
            {followLoading ? '...' : isFollowing ? (hoveringUnfollow ? 'Unfollow' : 'Following') : '+ Follow'}
          </button>
        </>
      )}
    </div>
  )
}
